import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import api from '@/lib/api';
import type { TodoItemResponse } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { useCompletedTimeline, useTodoList } from '../hooks';
import { CompletedTimeline } from '../components';

export function CompletedTimelinePage() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { list } = useTodoList(listId!);

  const isOwner = list ? list.ownerId === user?.userId : false;
  const hasEditAccess = isOwner || list?.userRole === 'editor';
  const [filterByCollaboratorId, setFilterByCollaboratorId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { items, loading, error, loadMore, hasMore } = useCompletedTimeline({
    listId: listId!,
    completedByUserId: filterByCollaboratorId ?? undefined,
  });

  const handleUncomplete = async (item: TodoItemResponse) => {
    const sectionExists =
      item.sectionId && list?.sections.some((s) => s.sectionId === item.sectionId);
    await api.updateTodoItem(listId!, item.itemId, {
      status: 'pending',
      ...(item.sectionId && !sectionExists ? { sectionId: null } : {}),
    });
    queryClient.invalidateQueries({ queryKey: queryKeys.todoLists.detail(listId!) });
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/a/arche_todo_lists/${listId}`)}
          aria-label="Back to list"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">Completed Items</h1>
      </div>

      {isOwner && list?.collaborators && list.collaborators.length > 0 && (
        <div className="flex items-center gap-2">
          <Select
            value={filterByCollaboratorId ?? 'all'}
            onValueChange={(v) => setFilterByCollaboratorId(v === 'all' ? null : v)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All collaborators" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All collaborators</SelectItem>
              {list.collaborators.map((c) => (
                <SelectItem key={c.userId} value={c.userId}>
                  {c.username}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No completed items yet</p>
        </div>
      ) : (
        <CompletedTimeline
          items={items}
          sections={list?.sections ?? []}
          onLoadMore={loadMore}
          hasMore={hasMore}
          loading={loading}
          showCompletedBy
          onUncomplete={hasEditAccess ? handleUncomplete : undefined}
        />
      )}
    </div>
  );
}
